import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { FocusKeyManager } from '@angular/cdk/a11y';
import { getDecade } from 'date-fns';
import { Subscription } from 'rxjs';
import { ISelectedRange } from '../utils/types';
import { PhxFocusableItemDirective } from '../phx-focusable-item.directive';
import { KeyboardKeys } from '../../../helpers/KeyboardKeys.enum';

@Component({
  selector: 'phx-year-picker-body',
  templateUrl: './phx-year-picker-body.component.html',
  styleUrls: ['./phx-year-picker-body.component.scss'],
  host: {
    class: 'phx-year-picker-body',
    '[tabIndex]': '0',
    '(focus)': 'onWrapperFocus()',
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhxYearPickerBodyComponent implements OnInit, OnDestroy {
  /** Current selected date */
  @Input() public phxSelectedDate: Date | ISelectedRange | null = null;
  /** Currently visible date */
  @Input() public phxVisibleDate: Date | ISelectedRange | null = null;

  /** Emits when an year is selected */
  @Output() public phxSelectionChange = new EventEmitter<Date | null>();

  @ViewChildren(PhxFocusableItemDirective)
  private _focusableItems: QueryList<PhxFocusableItemDirective>;
  private _focusableItemsSubscription: Subscription;
  private _keyManager: FocusKeyManager<PhxFocusableItemDirective>;

  public startingYearOfDecade = getDecade(new Date());
  public years: Date[] = [];

  ngOnInit(): void {
    this.generateDecadeYears();
  }

  ngAfterViewInit(): void {
    this._focusableItemsSubscription = this._focusableItems.changes.subscribe(() => {
      this._keyManager = new FocusKeyManager(this._focusableItems)
        .skipPredicate((item: PhxFocusableItemDirective) => item.skip)
        .withWrap();
    });
    this._focusableItems.notifyOnChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { phxVisibleDate } = changes;
    if (phxVisibleDate?.currentValue) {
      this.setupCurrentDecade();
      this.generateDecadeYears();
    }
  }

  ngOnDestroy(): void {
    this._focusableItemsSubscription.unsubscribe();
  }

  public onWrapperFocus(): void {
    this._keyManager.setFirstItemActive();
  }

  public onSelectYear(year: Date): void {
    this.phxSelectionChange.emit(year);
  }

  public onKeydown(year: Date, event: KeyboardEvent): void {
    event.preventDefault();
    switch (event.code) {
      case KeyboardKeys.ARROW_DOWN:
      case KeyboardKeys.ARROW_LEFT:
        this._keyManager.setPreviousItemActive();
        break;
      case KeyboardKeys.ARROW_UP:
      case KeyboardKeys.ARROW_RIGHT:
        this._keyManager.setNextItemActive();
        break;
      case KeyboardKeys.ENTER:
      case KeyboardKeys.SPACE:
        this.onSelectYear(year);
        break;
    }
  }

  public isSelectedYear(year: Date): boolean {
    if (!this.phxSelectedDate) return false;
    if (this.phxSelectedDate instanceof Date) {
      return this.phxSelectedDate.getFullYear() === year.getFullYear();
    } else if (this.isSelectedStartDate()) {
      if (this.isSelectedEndDate()) {
        return (
          this.phxSelectedDate.start.getFullYear() === year.getFullYear() ||
          this.phxSelectedDate.end.getFullYear() === year.getFullYear()
        );
      }
      return this.phxSelectedDate.start.getFullYear() === year.getFullYear();
    }
    return false;
  }

  public isCurrentYear(year: Date): boolean {
    const currentYear = new Date().getFullYear();
    return year.getFullYear() === currentYear;
  }

  private generateDecadeYears(): void {
    if (this.years.length > 0) {
      this.years.length = 0;
    }
    const startYear = this.startingYearOfDecade;
    for (let i = 0; i < 10; i++) {
      this.years.push(new Date(startYear + i, 0, 1));
    }
  }

  private setupCurrentDecade(): void {
    if (this.phxVisibleDate instanceof Date) {
      this.startingYearOfDecade = getDecade(this.phxVisibleDate);
    } else if (this.isSelectedStartDate()) {
      this.startingYearOfDecade = getDecade(this.phxVisibleDate.start);
    }
  }

  private isSelectedStartDate(): boolean {
    return 'start' in this.phxSelectedDate;
  }

  private isSelectedEndDate(): boolean {
    return 'end' in this.phxSelectedDate;
  }
}
